"use client";

import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import OptionItemEditModal from "./OptionItemEditModal";
import { ProductsService } from "@/services/ProductsService";
import { FileService } from "@/services/FileService";

interface OptionItemRowProps {
  groupIndex: number;
  itemIndex: number;
  removeItem: () => void;
  id: string;
}

const getProductName = async (id: string | null | undefined) => {
  if (!id) return "Sem produto associado";
  try {
    const product = await new ProductsService().findById(id);
    return product.name;
  } catch {
    return "Sem produto associado";
  }
};

export default function OptionItemRow({
  groupIndex,
  itemIndex,
  removeItem,
  id,
}: OptionItemRowProps) {
  const { watch } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productName, setProductName] = useState("Sem produto associado");
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const item = watch(`additional.${groupIndex}.items.${itemIndex}`);

  useEffect(() => {
    getProductName(item?.productId).then(setProductName);
  }, [item?.productId]);

  useEffect(() => {
    let url: string | null = null;
    if (item?.file) {
      url = URL.createObjectURL(item.file);
      setImageUrl(url);
    } else if (item?.fileId) {
      new FileService()
        .findById(item.fileId)
        .then((file) => setImageUrl(file.url))
        .catch(() => setImageUrl(null));
    } else {
      setImageUrl(null);
    }

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [item?.file, item?.fileId]);

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-2 p-2 bg-white rounded-md border hover:bg-gray-50"
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="cursor-grab"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </Button>
        <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
          {imageUrl ? (
            <img src={imageUrl} alt={item.name} className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="h-5 w-5 text-gray-400" />
          )}
        </div>
        <div className="flex-grow">
          <p className="font-medium text-sm">{item.name || "Novo Item"}</p>
          <p className="text-xs text-gray-500">{productName}</p>
        </div>
        <p className="text-sm font-mono text-gray-700">
          + R${" "}
          {Number(item.unitPrice || 0)
            .toFixed(2)
            .replace(".", ",")}
        </p>
        <div className="flex items-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setIsModalOpen(true)}
          >
            <Pencil className="h-4 w-4 text-blue-600" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={removeItem}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      <OptionItemEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        groupIndex={groupIndex}
        itemIndex={itemIndex}
      />
    </>
  );
}
